import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import moment, { type Moment } from "moment";

import type { JobCostingBudgetColumns } from "@btnet/services-btnet/handlers/models/jobCostingBudgetColumns";

import {
    type BTEntityAction,
    BTSelectItem,
    type IBaseEntity,
    type IDropDownItem,
    mapBTServiceDropdownToSelectItem,
    type ServiceValidation,
} from "types/apiResponse/apiResponse";
import {
    type ActivationStatus,
    CustomFieldAssociatedType,
    JobContractType,
    type LeadStatus,
} from "types/enum";

import { alphabetize, getSelectedValue, getSelectedValues } from "utilities/form/form";

import { type IApprovalUserExtraData } from "commonComponents/approvals/ApprovalUserSelect/ApprovalUserSelect.type";
import {
    type IEntityExtraData,
    type LinkingAction,
} from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import {
    type AccountingEntity,
    type BTPrimaryEntityOption,
    getAccountingContactFromEntity,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import {
    AddressEntity,
    AddressFormValues,
    type AddressServiceObject,
    GetAddressServiceObject,
    type IHasAddress,
    type LocationEntity,
} from "commonComponents/entity/address/Address/Address.api.types";
import { getColorIdFromHex } from "commonComponents/entity/colorPicker/ColorPicker/ColorPicker.types";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    CustomFieldPrintItem,
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    ViewingAccessEntity,
    type ViewingAccessFormValues,
} from "commonComponents/entity/job/ViewingAccess/ViewingAccess.types";
import { type IMapPosition, PinTypes } from "commonComponents/entity/map/Map.types";
import {
    type INotificationPreference,
    type INotificationPreferenceData,
    type INotificationPreferenceRow,
} from "commonComponents/entity/notificationPreferences/NotificationPreferencesTable.api.types";
import { type UserWithConflicts } from "commonComponents/entity/scheduleConflicts/ScheduleConflict.api.types";
import { type JobsiteLinkingTypes } from "commonComponents/financial/Common/Accounting.types";
import {
    type IOnlinePaymentLimitsFormValues,
    type OnlinePaymentLimitsApiData,
    OnlinePaymentLimitsEntity,
} from "commonComponents/financial/OnlinePaymentLimits/OnlinePaymentLimits.api.types";
import { PrintHeaderAPI } from "commonComponents/print/PrintHeader/PrintHeader.types";

import { type BudgetColumn } from "entity/budget/Budget/BudgetContainer/BudgetContainer.api.types";
import { type RiskInsuranceStatus } from "entity/builderRiskInsurance/BuilderRiskInsuranceStatus/BuilderRiskInsuranceStatus.api.types";
import {
    ContactEntity,
    ContactEntityInfo,
    type IContactDetailCardInfo,
} from "entity/contact/Contact/Contact.api.types";
import { type AdjustPercentTypes } from "entity/estimate/AdjustPercentMarkup/AdjustPercentMarkup.api.types";
import {
    type IDraw,
    type IDrawScheduleValues,
} from "entity/ownerInvoice/drawSchedule/DrawScheduleService/DrawScheduleService";
import { JobTaxVersion } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

import { type TaxGroupServiceListItemExtraData } from "../../commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";

export enum JobDetailTab {
    Job = "1",
    Owner = "2",
    InternalUsers = "3",
    SubsVendors = "4",
    Options = "5",
    // This tab is no longer being used since AccountingLinkingCard moved from Accounting tab to main Job tab
    // Accounting = "6",
    BRI = "7",
}

export enum JobFundingType {
    ClientFunded = 0,
    ConstructionLoan = 1,
    PreferNotToAnswer = 2,
}

export interface IDefaultBillApprover {
    globalUserId: number;
    name: string;
}

export interface IDefaultBillApproversInfo {
    jobApprovers: IDefaultBillApprover[];
    eligibleJobBillApprovers: BTSelectItem<IApprovalUserExtraData>[];
}

export interface IJobFormValues {
    jobInfo: JobInfoFormValues;
    ownerInfo: OwnerInfoFormValues;
    viewingAccess?: ViewingAccessFormValues;
    accountingEntityLinkingType: JobsiteLinkingTypes;
    createNewAccountingEntityOption: LinkingAction;
    includeOtherAccountingCostsInBudget: boolean;
    includeQBCostsInProjectedCostsCalculation: boolean;
    customFields: ICustomFieldFormValues[];
    notificationPreferences: INotificationPreferenceRow[];
    primaryAccountingOption: string;
    secondaryAccountingOption: string;
    tertiaryAccountingOption: string;
    accountingContactId: string;
    jobBillAdditionalApproverGlobalUserIds: number[];
    billPayPaymentMethodId: number | null;
}

interface IContractPriceData {
    value: number;
    maxValue: number | null;
    minValue: number | null;
}

export class ContractPrice {
    constructor(data: IContractPriceData) {
        this.value = data.value;
        this.max = data.maxValue ?? undefined;
        this.min = data.minValue ?? undefined;
    }

    value: number;
    max: number | undefined;
    min: number | undefined;
}

export class JobInfoFormValues {
    constructor(data: JobInfoEntity, isNewJob: boolean = false) {
        this.jobName = data.jobName;
        this.builderName = data.builderName;
        this.groupedProjectType = getSelectedValue(data.groupedProjectTypes);
        this.contractType = getSelectedValue(data.contractTypes);
        this.jobStatus = getSelectedValue(data.jobStatus);
        this.projectManagers = getSelectedValues(data.projectManagers);
        this.jobGroups = getSelectedValues(data.jobGroups);
        this.jobsitePrefix = data.jobsitePrefix;
        this.lotInfo = data.lotInfo;
        this.permitNumber = data.permitNumber;
        this.contractPrice = data.contractPrice?.value ?? 0;
        this.address = new AddressFormValues(data);
        this.workDays = getSelectedValues(data.workDays);
        this.totalArea = data.totalArea;
        this.notifyProjectManager = false;
        this.internalNotes = data.internalNotes;
        this.subNotes = data.subNotes;
        this.projectedStartDate = data.projectedStartDate;
        this.projectedCompletionDate = data.projectedCompletionDate;
        this.actualStartDate = data.actualStartDate;
        this.actualCompletionDate = data.actualCompletionDate;
        this.syncActualStartEnd = data.syncActualStartEnd;
        this.enabled = data.enabled;

        const selectedColorItem = data.jobColor.filter((item) => item.selected)[0];
        this.jobColor =
            selectedColorItem && selectedColorItem.extraData ? selectedColorItem.extraData.hex : "";

        this.individualPOLimit = new POLimit(data.individualPOLimit).value;
        this.totalPOLimit = new POLimit(data.totalPOLimit).value;
        this.enableGeofencing = data.enableGeofencing;
        this.includeTimeClockForBudget = data.includeTimeClockForBudget;
        this.includeTimeClockForCosts = data.includeTimeClockForCosts;
        this.includeAllowances = data.includeAllowances;
        this.isAiaBillingJob = data.isAiaBillingJob;
        this.updateRunningTotalForSelection = data.updateRunningTotalForSelection;
        this.isTemplate = data.isTemplate;
        this.templateName = data.templateName;
        this.remainingDaysToDelete = data.remainingDaysToDelete ?? undefined;
        this.defaultTaxGroupId = data.defaultTaxGroupId ?? NoTaxId;
        this.contractType = getSelectedValue(data.contractTypes);
        if (data.additionalInfo?.financialSettings) {
            this.drawScheduleValues = {
                draws: data.additionalInfo.financialSettings.drawScheduleConfiguration?.draws ?? [],
                numberOfDraws:
                    data.additionalInfo.financialSettings.drawScheduleConfiguration?.draws.length ??
                    0,
            };
            if (
                isNewJob &&
                this.contractType === JobContractType.FixedPrice &&
                data.fixedPriceDefaultMarkup
            ) {
                this.adjustMarkup = data.fixedPriceDefaultMarkup;
            } else if (
                isNewJob &&
                this.contractType === JobContractType.OpenBook &&
                data.openBookDefaultMarkup
            ) {
                this.adjustMarkup = data.openBookDefaultMarkup;
            } else {
                this.adjustMarkup = new AdjustPercentMarkup(data.additionalInfo.financialSettings);
            }
        }
        this.fundingType = data.additionalInfo?.financialSettings?.fundingType;
        this.defaultBillRetainagePercent =
            data.additionalInfo?.financialSettings?.defaultBillRetainagePercent;
        this.defaultInvoiceRetainagePercent =
            data.additionalInfo?.financialSettings?.defaultInvoiceRetainagePercent;
        this.defaultProjectionReference =
            data.additionalInfo?.financialSettings?.defaultProjectionReference;
    }

    jobName: string;
    builderName: string;
    groupedProjectType: number;
    jobStatus: number;
    projectManagers: number[];
    jobGroups: number[];
    jobsitePrefix: string;
    lotInfo: string;
    permitNumber: string;
    contractPrice: number;
    fundingType?: JobFundingType;
    defaultBillRetainagePercent?: number;
    defaultInvoiceRetainagePercent?: number;
    defaultProjectionReference?: number;
    workDays: number[];
    totalArea: number | null;
    notifyProjectManager: boolean;
    internalNotes: string | null;
    subNotes: string | null;
    projectedStartDate: moment.Moment | null;
    projectedCompletionDate: moment.Moment | null;
    actualStartDate: moment.Moment | null;
    actualCompletionDate: moment.Moment | null;
    syncActualStartEnd: boolean;
    enabled: boolean;
    individualPOLimit: number | undefined | null;
    totalPOLimit: number | undefined | null;
    enableGeofencing: boolean;
    includeTimeClockForBudget: boolean;
    includeTimeClockForCosts: boolean;
    includeAllowances: boolean;
    isAiaBillingJob: boolean;
    updateRunningTotalForSelection: boolean;
    isTemplate: boolean;
    templateName: string;
    jobColor: string;
    address: AddressFormValues;
    remainingDaysToDelete?: number;
    defaultTaxGroupId: number;
    contractType?: number;
    adjustMarkup?: AdjustPercentMarkup;
    drawScheduleValues?: IDrawScheduleValues;
}

export class AdjustPercentMarkup {
    constructor(data: JobFinancialSettings) {
        this.defaultMarkup = data.defaultMarkup;
        this.defaultMargin = data.defaultMargin;
        this.markupMarginPercentType = data.markupMarginPercentType;
        this.costTypesMarkup = data.costTypesMarkup ?? [];
    }
    defaultMarkup?: number;
    defaultMargin?: number;
    markupMarginPercentType: number;
    costTypesMarkup: ICostTypesMarkup[];
}

export enum JobStatus {
    GeneralJob = -4,
    Closed = 0,
    Open = 1,
    Presale = 3,
    Warranty = 4,
}

export class OwnerInfoFormValues {
    constructor(data: OwnerInfoEntity) {
        this.contacts = data.contacts.map((c) => new ContactEntityInfo(c));
        this.showJobPrice = data.showJobPrice;
        this.showClientRemainingInvoiceBalance = data.showClientRemainingInvoiceBalance;
        this.showClientBudgetDifference = data.showClientBudgetDifference;
        this.showBudget = data.showBudget;
        this.showPOsAndBills = data.showPOsAndBills;
        this.addWarrantyClaims = data.addWarrantyClaims;
        this.limitOwnerCalendar = getSelectedValue(data.limitOwnerCalendar);
        this.canSubmitCORequest = data.canSubmitCORequest;
        this.peekLockedSelections = data.peekLockedSelections;
        this.seePaymentsTab = data.seePaymentsTab;
        this.showCalendarPhases = data.showCalendarPhases;
        this.showScheduleItems = data.showScheduleItems;
        this.addedWarrantyClaimsEndDate =
            data.addedWarrantyClaimsEndDate !== undefined ? data.addedWarrantyClaimsEndDate : null;
        this.existingLinkedJobs = getSelectedValues(data.linkedJobs);
        this.newLinkedJobs = [];
        this.paymentOptions = { ...data.paymentOptionsSetup };
        this.showProjectManagerPhoneNumbersToOwner = data.showProjectManagerPhoneNumbersToOwner;
        this.budgetDisplayPreference = getSelectedValues(
            data.budgetDisplayPreference.filter((x) => !x.extraData?.disabled),
        );
        this.jobCostingBudgetDisplayPreference = getSelectedValues(
            data.jobCostingBudgetDisplayPreference.filter((x) => !x.extraData?.disabled),
        );
        this.showJobCostingBudgetToOwner = data.showJobCostingBudgetToOwner;
        this.showRelatedTagsToOwnerOnJobCostingBudget =
            data?.showRelatedTagsToOwnerOnJobCostingBudget ?? false;
        this.accountingContacts =
            data.contacts.map((contact) => getAccountingContactFromEntity(contact)) ?? [];
    }
    contacts: IContactDetailCardInfo[];
    accountingContacts: BTPrimaryEntityOption[];
    showJobPrice: boolean;
    showClientRemainingInvoiceBalance: boolean;
    showClientBudgetDifference: boolean;
    showBudget: boolean;
    showPOsAndBills: boolean;
    addWarrantyClaims: boolean;
    limitOwnerCalendar: number;
    canSubmitCORequest: boolean;
    peekLockedSelections: boolean;
    seePaymentsTab: boolean;
    showCalendarPhases: boolean;
    showScheduleItems: boolean;
    addedWarrantyClaimsEndDate: moment.Moment | null;
    existingLinkedJobs: number[];
    newLinkedJobs: number[];
    resendEmailInvite: boolean;
    paymentOptions: IOnlinePaymentLimitsFormValues;
    showProjectManagerPhoneNumbersToOwner: boolean;
    budgetDisplayPreference: number[];
    jobCostingBudgetDisplayPreference: number[];
    showJobCostingBudgetToOwner: boolean;
    showRelatedTagsToOwnerOnJobCostingBudget: boolean;
}

export class OwnerInfoSaveRequest {
    constructor(
        entity: OwnerInfoEntity,
        values: OwnerInfoFormValues,
        notificationPreferences: INotificationPreferenceRow[],
    ) {
        this.contacts = values.contacts?.map((contact) => contact.id) ?? [];
        this.showJobPrice = values.showJobPrice;
        this.showClientRemainingInvoiceBalance = values.showClientRemainingInvoiceBalance;
        this.showClientBudgetDifference = values.showClientBudgetDifference;
        this.showPOsAndBills = values.showPOsAndBills;
        this.addWarrantyClaims = values.addWarrantyClaims;
        this.limitOwnerCalendar = values.limitOwnerCalendar;
        this.canSubmitCORequest = values.canSubmitCORequest;
        this.peekLockedSelections = values.peekLockedSelections;
        this.seePaymentsTab = values.seePaymentsTab;
        this.showPhases = values.showCalendarPhases;
        this.showScheduleItems = values.showScheduleItems;
        this.addedWarrantyClaimsEndDate = values.addedWarrantyClaimsEndDate;
        this.linkedJobs = values.existingLinkedJobs.concat(values.newLinkedJobs);
        this.allowCreditCards = values.paymentOptions.allowCreditCards;
        this.minCreditCard = values.paymentOptions.minCreditCard;
        this.maxCreditCard = values.paymentOptions.maxCreditCard;
        this.passCreditCardFeesToOwner = values.paymentOptions.passCreditCardFeesToOwner;
        this.passACHFeesToClient = values.paymentOptions.passACHFeesToClient;
        this.allowChecks = values.paymentOptions.allowChecks;
        this.minCheck = values.paymentOptions.minCheck;
        this.maxCheck = values.paymentOptions.maxCheck;
        this.allowPartialPayments = values.paymentOptions.allowPartialPayments;
        this.resendEmailInvite = values.resendEmailInvite;
        this.showProjectManagerPhoneNumbersToOwner = values.showProjectManagerPhoneNumbersToOwner;
        this.notificationPreferences = notificationPreferences
            .filter((np) => !np.isCategoryRow && !np.isGroupRow)
            .map((row) => {
                const pref: INotificationPreference = {
                    emailEnabled: row.emailEnabled!,
                    textEnabled: row.textEnabled!,
                    pushEnabled: row.pushEnabled,
                    allUsersEnabled: row.allUsersEnabled,
                    name: row.name,
                    type: row.type!,
                };
                return pref;
            });
        this.budgetDisplayPreference = values.budgetDisplayPreference;
        this.jobCostingBudgetDisplayPreference = values.jobCostingBudgetDisplayPreference;
        this.showBudget =
            values.showBudget || (values.showJobCostingBudgetToOwner && entity.shouldHideOldBudget);
        this.showJobCostingBudgetToOwner = values.showJobCostingBudgetToOwner;
        this.showRelatedTagsToOwnerOnJobCostingBudget =
            values?.showRelatedTagsToOwnerOnJobCostingBudget ?? false;
    }
    contacts: number[];
    showJobPrice: boolean;
    showClientRemainingInvoiceBalance: boolean;
    showClientBudgetDifference: boolean;
    showBudget: boolean;
    showPOsAndBills: boolean;
    addWarrantyClaims: boolean;
    limitOwnerCalendar: number;
    canSubmitCORequest: boolean;
    peekLockedSelections: boolean;
    seePaymentsTab: boolean;
    showPhases: boolean;
    showScheduleItems: boolean;
    addedWarrantyClaimsEndDate: moment.Moment | null;
    linkedJobs: number[];
    allowPartialPayments: boolean;
    allowCreditCards: boolean;
    passCreditCardFeesToOwner: boolean;
    passACHFeesToClient: boolean;
    minCreditCard: number | null;
    maxCreditCard: number | null;
    allowChecks: boolean;
    minCheck: number | null;
    maxCheck: number | null;
    resendEmailInvite: boolean;
    notificationPreferences: INotificationPreference[];
    showProjectManagerPhoneNumbersToOwner: boolean;
    budgetDisplayPreference: number[];
    jobCostingBudgetDisplayPreference: number[];
    showJobCostingBudgetToOwner: boolean;
    showRelatedTagsToOwnerOnJobCostingBudget: boolean;
}
interface IBillRateUpsert {
    globalUserId: number;
    billRate: number;
}

export class JobViewingAccessSaveRequest {
    constructor(values: ViewingAccessFormValues) {
        this.users = values.users
            .filter((user) => {
                return user.hasAccess;
            })
            .map((user) => user.accessorId);
        this.subs = values.subs
            .filter((sub) => {
                return sub.hasAccess;
            })
            .map((sub) => sub.accessorId);
        this.userNotifications = values.users
            .filter((user) => user.hasNotificationAccess)
            .map((user) => user.accessorId);
        this.userBillRates = values.users
            .filter((user) => {
                return user.hasAccess && user.jobDefaultBillRate != null;
            })
            .map((user) => ({
                globalUserId: user.accessorId,
                billRate: user.jobDefaultBillRate as number,
            }));
    }
    users: number[];
    subs: number[];
    userNotifications: number[];
    userBillRates: IBillRateUpsert[];
}

export class JobEntity implements IBaseEntity, IHasCustomFieldContainer {
    constructor(data: any) {
        this.builderId = data.builderId;
        this.globalUserId = data.globalUserId;
        this.hasAdminPermissions = data.hasAdminPermissions;
        this.taxGroups = data.taxGroups
            ? (mapBTServiceDropdownToSelectItem<TaxGroupServiceListItemExtraData>(data.taxGroups)[0]
                  ?.children ?? [])
            : [];
        this.jobInfo = new JobInfoEntity(data.jobInfo, data.lead);
        this.ownerInfo = new OwnerInfoEntity(data);
        if (data.viewingAccess) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            this.viewingAccess = new ViewingAccessEntity(data.viewingAccess);
        }
        if (data.jobId) {
            this.jobId = data.jobId;
        }

        this.canAdd = data.canAdd;
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canViewPrice = data.canViewPrice;
        this.canViewSchedule = data.canViewSchedule;
        this.canViewTimeClock = data.canViewTimeClock;
        this.canViewSelections = data.canViewSelections;
        this.canViewWarranty = data.canViewWarranty;
        this.isMerchantSetup = data.isMerchantSetup;
        this.canRemoveAllowances = data.canRemoveAllowances;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- constructor takes untyped API data
        this.canDisableAiaBilling = data.canDisableAiaBilling ?? true;
        this.canViewAccounting = data.canViewAccounting;
        this.showBudgetOptions = data.showBudgetOptions;
        this.canSaveSelect = data.canSaveSelect;
        this.canAddSubs = data.canAddSubs;
        this.canAddInternalUsers = data.canAddInternalUsers;
        this.allowJobsUnderCustomer = data.allowJobsUnderCustomer;
        this.linkingOptions =
            data.linkingOptions &&
            data.linkingOptions.map((item: any) => new BTSelectItem<IEntityExtraData>(item));
        this.canViewPurchaseOrders = data.canViewPurchaseOrders;
        this.canViewChangeOrders = data.canViewChangeOrders;
        this.canViewPayments = data.canViewPayments;
        this.canViewRiskInsurance = data.canViewRiskInsurance;
        this.riskInsuranceStatus = data.riskInsuranceStatus;
        this.customFields = data.jobInfo.customFields;
        this.customFieldAssociatedType = CustomFieldAssociatedType.JobsJobInfo;
        this.customFieldOptions = data.customFieldOptions;
        // Definition config is admin-only; the Job API does not send this flag, so
        // default it off and let the render site derive it (useCanConfigureCustomFields).
        this.customFieldsCanConfigure = false;
        this.hasTimeClockItems = data.hasTimeClockItems;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.billRateEnabledForJob = data.billRateEnabledForJob;
        this.hasAllowances = data.hasAllowances;
        this.hasPurchaseOrderPriceViewing = data.hasPurchaseOrderPriceViewing;
        this.createdJobCountForBuilder = data.createdJobCountForBuilder;
        if (data.printHeader) {
            this.printHeader = new PrintHeaderAPI(data.printHeader);
        }
        if (data.customFieldsPrint) {
            this.customFieldsPrint = data.customFieldsPrint.map(
                (x: any) => new CustomFieldPrintItem(x),
            );
        }
        this.hasBudgetAccess = data.hasBudgetAccess;
        this.isBuilderConnectedToAccounting = data.isBuilderConnectedToAccounting;
        this.worksheetLocked = data.worksheetLocked;
        this.isEstimateSentToBudget = data.isEstimateSentToBudget;
        this.effectiveTaxVersion = data.effectiveTaxVersion ?? JobTaxVersion.None;
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob ?? false;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- constructor takes untyped API data
        this.isStandaloneTemplate = data.isStandaloneTemplate ?? false;
        this.jobStatus = data.jobStatus ?? JobStatus.Open;
        this.defaultBillApprovers = data.defaultBillApprovers
            ? {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                  jobApprovers: data.defaultBillApprovers.jobApprovers ?? [],
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  eligibleJobBillApprovers:
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                      data.defaultBillApprovers.eligibleJobBillApprovers?.map(
                          (item: IDropDownItem) => new BTSelectItem<IApprovalUserExtraData>(item),
                      ) ?? [],
              }
            : null;
    }

    id: number;
    builderId: number;
    globalUserId: number;
    jobId: number;
    jobInfo: JobInfoEntity;
    ownerInfo: OwnerInfoEntity;
    viewingAccess?: ViewingAccessEntity;
    jobPermissionWizardEntity?: JobPermissionWizardEntity;

    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canViewPrice: boolean;
    canViewSchedule: boolean;
    canViewTimeClock: boolean;
    canViewSelections: boolean;
    canViewWarranty: boolean;
    canRemoveAllowances: boolean;
    canDisableAiaBilling: boolean;
    canViewAccounting: boolean;
    isMerchantSetup: boolean;
    showBudgetOptions: boolean;
    canSaveSelect: boolean;
    canAddSubs: boolean;
    canAddInternalUsers: boolean;
    canViewPurchaseOrders: boolean;
    canViewChangeOrders: boolean;
    canViewPayments: boolean;
    allowJobsUnderCustomer: boolean;
    linkingOptions: BTSelectItem<IEntityExtraData>[];
    canViewRiskInsurance: boolean;
    customFields: ICustomFieldResponse[];
    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldOptions: ICustomFieldOptionsList;
    customFieldsCanConfigure: boolean;

    hasTimeClockItems: boolean;
    billRateEnabledForJob: boolean;
    hasAllowances: boolean;
    riskInsuranceStatus: RiskInsuranceStatus;
    accountingEntity: AccountingEntity | null;
    hasPurchaseOrderPriceViewing: boolean;
    createdJobCountForBuilder?: number;
    printHeader?: PrintHeaderAPI;
    customFieldsPrint: CustomFieldPrintItem[];
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[];
    hasAdminPermissions: boolean;
    hasBudgetAccess: boolean;
    isBuilderConnectedToAccounting: boolean;
    worksheetLocked: boolean;
    isEstimateSentToBudget: boolean;
    effectiveTaxVersion: JobTaxVersion;
    isSelectionsReimaginedJob: boolean;
    isStandaloneTemplate: boolean;
    jobStatus: JobStatus;
    defaultBillApprovers: IDefaultBillApproversInfo | null;
}

export class JobInfoEntity implements IHasAddress {
    constructor(data: any, leadInfo: any) {
        this.jobName = data.jobName.value;
        this.groupedProjectTypes = [];
        if (data.groupedProjectType) {
            /**
             * groupedProjectTypes can have a mix of grouped & ungrouped items, where ungrouped items appear in a nameless group.
             * Here, if a group has no name, we pull its children out in to the root of the list, so that they appear as ungrouped BTSelectItems.
             */
            data.groupedProjectType.value.forEach((group: any) => {
                if (!group.name) {
                    group.options.forEach((child: any) => {
                        this.groupedProjectTypes.push(new BTSelectItem(child));
                    });
                } else {
                    this.groupedProjectTypes.push(new BTSelectItem(group));
                }
            });
        }

        this.contractTypes = data.contractType.value.map((item: any) => new BTSelectItem(item));
        this.projectManagers = data.projectManagers.value.map(
            (item: any) => new BTSelectItem(item),
        );
        this.jobStatus = data.jobStatus.value.map((item: any) => new BTSelectItem(item));
        this.jobsitePrefix = data.jobsitePrefix ? data.jobsitePrefix.value : "";
        this.lotInfo = data.lotInfo.value;
        this.permitNumber = data.permitNumber.value;
        this.dateOpened =
            data.dateOpened && data.dateOpened.value ? moment(data.dateOpened.value) : null;
        this.openedByName = data.openedByName.value;
        this.openedById = data.openedById.value;
        if (leadInfo) {
            this.relatedLeadId = leadInfo.leadId;
            this.leadStatus = leadInfo.leadStatus;
            this.leadTitle = leadInfo.leadTitle;
        }
        this.contractPrice = data.contractPrice
            ? new ContractPrice(data.contractPrice as IContractPriceData)
            : undefined;
        this.jobGroups = data.jobGroups
            ? alphabetize(data.jobGroups.value.map((item: any) => new BTSelectItem(item)))
            : [];
        this.address = new AddressEntity(data.address);
        this.workDays = data.workDays
            ? data.workDays.value.list.map((item: any) => new BTSelectItem(item))
            : [];
        this.hasScheduleItems = data.hasScheduleItems;

        if (data.totalArea) {
            this.totalArea = data.totalArea.value;
            this.totalAreaLabel = data.totalArea.title;
        }
        this.internalNotes = data.internalNotes ? data.internalNotes.value : "";
        this.subNotes = data.subNotes.value;
        this.projectedStartDate =
            data.projectedStartDate && data.projectedStartDate.value
                ? moment(data.projectedStartDate.value)
                : null;
        this.projectedCompletionDate =
            data.projectedCompletionDate && data.projectedCompletionDate.value
                ? moment(data.projectedCompletionDate.value)
                : null;
        this.actualStartDate =
            data.actualStartDate && data.actualStartDate.value
                ? moment(data.actualStartDate.value)
                : null;
        this.actualCompletionDate =
            data.actualCompletionDate && data.actualCompletionDate.value
                ? moment(data.actualCompletionDate.value)
                : null;
        this.syncActualStartEnd = data.syncActualStartEnd ?? false;
        this.scheduleStart = data.scheduleStart ? moment(data.scheduleStart) : undefined;
        this.scheduleEnd = data.scheduleEnd ? moment(data.scheduleEnd) : undefined;
        this.enabled = data.workDays ? data.workDays.value.enabled : false;
        this.jobColor = data.jobColor
            ? data.jobColor.value.map((item: any) => new BTSelectItem<JobColorExtraData>(item))
            : [];
        this.hasPurchaseOrderPriceViewing = data.hasPurchaseOrderPriceViewing ?? false;
        if (data.individualPOLimit) {
            this.individualPOLimit = new POLimit(data.individualPOLimit);
        }
        if (data.totalPOLimit) {
            this.totalPOLimit = new POLimit(data.totalPOLimit);
        }
        this.enableGeofencing = data.enableGeofencing?.value;
        this.includeTimeClockForBudget = data.includeTimeClockForBudget
            ? data.includeTimeClockForBudget.value
            : undefined;
        this.includeTimeClockForCosts = data.includeTimeClockForCosts
            ? data.includeTimeClockForCosts.value
            : undefined;
        this.includeAllowances = data.includeAllowances ? data.includeAllowances.value : undefined;
        this.isAiaBillingJob = data.isAiaBillingJob ? data.isAiaBillingJob.value : undefined;
        this.updateRunningTotalForSelection = data.updateRunningTotalForSelection
            ? data.updateRunningTotalForSelection.value
            : undefined;
        this.isTemplate = data.isTemplate ? data.isTemplate.value : undefined;
        this.templateName = data.templateName ? data.templateName : undefined;
        this.builderLocation = data.builderLocation
            ? { lat: data.builderLocation.latitude, lng: data.builderLocation.longitude }
            : null;
        this.builderName = data.builderName && data.builderName.value;
        this.remainingDaysToDelete = data.remainingDaysToDelete ?? undefined;
        this.costItemResetMessage = data.costItemResetMessage ?? null;
        this.updateJobsitePricingWithCreditMemos = data.updateJobsitePricingWithCreditMemos;
        this.defaultTaxGroupId = data.defaultTaxGroupId ?? NoTaxId;
        if (data.additionalInfo) {
            this.additionalInfo = new JobInformation(data.additionalInfo);
        }
        if (data.fixedPriceDefaultMarkup) {
            this.fixedPriceDefaultMarkup = new AdjustPercentMarkup(data.fixedPriceDefaultMarkup);
        }
        if (data.openBookDefaultMarkup) {
            this.openBookDefaultMarkup = new AdjustPercentMarkup(data.openBookDefaultMarkup);
        }

        this.paymentMethods =
            data.paymentMethods?.map((item: IDropDownItem) => new BTSelectItem<number>(item)) ??
            null;
    }

    jobName: string;
    builderName: string;
    groupedProjectTypes: BTSelectItem[];
    contractTypes: BTSelectItem[];
    jobStatus: BTSelectItem[];
    projectManagers: BTSelectItem[];
    jobsitePrefix: string;
    lotInfo: string;
    permitNumber: string;
    dateOpened: moment.Moment | null;
    openedByName: string;
    openedById: number;
    relatedLeadId?: number;
    leadStatus?: LeadStatus;
    leadTitle?: string;
    contractPrice?: ContractPrice;
    address: AddressEntity;
    workDays: BTSelectItem[];
    hasScheduleItems: boolean;
    totalArea: number | null;
    totalAreaLabel: string;
    jobGroups: BTSelectItem[];
    internalNotes: string;
    subNotes: string;
    projectedStartDate: moment.Moment | null;
    projectedCompletionDate: moment.Moment | null;
    actualStartDate: moment.Moment | null;
    actualCompletionDate: moment.Moment | null;
    syncActualStartEnd: boolean;
    scheduleStart?: moment.Moment;
    scheduleEnd?: moment.Moment;
    enabled: boolean;
    jobColor: BTSelectItem<JobColorExtraData>[];
    hasPurchaseOrderPriceViewing: boolean;
    individualPOLimit?: POLimit;
    totalPOLimit?: POLimit;
    enableGeofencing: boolean;
    includeTimeClockForBudget: boolean;
    includeTimeClockForCosts: boolean;
    includeAllowances: boolean;
    isAiaBillingJob: boolean;
    updateRunningTotalForSelection: boolean;
    isTemplate: boolean;
    templateName: string;
    showBudgetOptions: boolean;
    builderLocation: IMapPosition | null;
    remainingDaysToDelete?: number;
    costItemResetMessage: string | null;
    updateJobsitePricingWithCreditMemos: boolean;
    defaultTaxGroupId: number;
    additionalInfo?: JobInformation;
    fixedPriceDefaultMarkup?: AdjustPercentMarkup;
    openBookDefaultMarkup?: AdjustPercentMarkup;
    paymentMethods: BTSelectItem<number>[] | null;
}

class JobInformation {
    constructor(data: any) {
        this.financialSettings = data.financialSettings;
    }
    financialSettings: JobFinancialSettings;
}

class JobFinancialSettings {
    constructor(data: Partial<JobFinancialSettings>) {
        this.hasSingleSelectCostTypes = data.hasSingleSelectCostTypes!;
        this.markupMarginPercentType = data.markupMarginPercentType!;
        this.defaultMargin = data.defaultMargin;
        this.defaultMarkup = data.defaultMarkup;
        this.costTypesMarkup = data.costTypesMarkup;
        this.isBetterWayFinancials = data.isBetterWayFinancials;
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob;
        this.fundingType = data.fundingType;
        this.defaultBillRetainagePercent = data.defaultBillRetainagePercent;
        this.defaultInvoiceRetainagePercent = data.defaultInvoiceRetainagePercent;
        this.drawScheduleConfiguration = data.drawScheduleConfiguration;
        this.defaultProjectionReference = data.defaultProjectionReference;
    }
    hasSingleSelectCostTypes: boolean;
    markupMarginPercentType: AdjustPercentTypes;
    defaultMarkup?: number;
    defaultMargin?: number;
    costTypesMarkup?: ICostTypesMarkup[];
    isBetterWayFinancials?: boolean;
    isSelectionsReimaginedJob?: boolean;
    fundingType?: JobFundingType;
    defaultBillRetainagePercent?: number;
    defaultInvoiceRetainagePercent?: number;
    drawScheduleConfiguration?: IDrawScheduleConfiguration;
    defaultProjectionReference?: number;
}

export class JobInfoSaveRequest {
    constructor(
        values: JobInfoFormValues,
        entity: JobInfoEntity,
        customFields: ICustomFieldFormValues[],
    ) {
        this.jobName = values.jobName;
        this.groupedProjectType = values.groupedProjectType;
        this.jobStatus = values.jobStatus;
        this.projectManagers = values.projectManagers;
        this.jobGroups = values.jobGroups;
        this.jobsitePrefix = values.jobsitePrefix;
        this.lotInfo = values.lotInfo;
        this.permitNumber = values.permitNumber;
        this.contractPrice = values.contractPrice || 0;
        this.totalArea = values.totalArea;
        this.notifyProjectManager = values.notifyProjectManager;
        this.internalNotes = values.internalNotes;
        this.subNotes = values.subNotes;
        this.projectedStartDate = values.projectedStartDate;
        this.projectedCompletionDate = values.projectedCompletionDate;
        this.actualStartDate = values.actualStartDate;
        this.actualCompletionDate = values.actualCompletionDate;
        this.syncActualStartEnd = values.syncActualStartEnd;
        this.enabled = values.enabled;
        this.jobColor = getColorIdFromHex(values.jobColor);
        this.address = GetAddressServiceObject(entity, values.address);
        this.workDays = values.workDays;
        this.individualPOLimit =
            values.individualPOLimit !== undefined ? values.individualPOLimit : null;
        this.totalPOLimit = values.totalPOLimit !== undefined ? values.totalPOLimit : null;
        this.enableGeofencing = values.enableGeofencing;
        this.includeTimeClockForBudget = values.includeTimeClockForBudget;
        this.includeTimeClockForCosts = values.includeTimeClockForCosts;
        this.includeAllowances = values.includeAllowances;
        this.isAiaBillingJob = values.isAiaBillingJob;
        this.removeAllowancesWasConfirmed = false;
        this.updateRunningTotalForSelection = values.updateRunningTotalForSelection;
        this.isTemplate = values.isTemplate;
        this.templateName = values.templateName;
        this.location = values.address.location!;
        this.customFields = customFields;
        this.defaultTaxGroupId = values.defaultTaxGroupId;
        this.contractType = values.contractType;
        this.markupMarginPercentType = values.adjustMarkup?.markupMarginPercentType ?? 0;
        this.costTypesMarkup = values.adjustMarkup?.costTypesMarkup;
        this.defaultMarkup = values.adjustMarkup?.defaultMarkup;
        this.defaultMargin = values.adjustMarkup?.defaultMargin;
        this.fundingType = values.fundingType;
        this.defaultBillRetainagePercent =
            values.defaultBillRetainagePercent !== undefined
                ? values.defaultBillRetainagePercent
                : null;
        this.defaultInvoiceRetainagePercent =
            values.defaultInvoiceRetainagePercent !== undefined
                ? values.defaultInvoiceRetainagePercent
                : null;
        this.defaultProjectionReference = values.defaultProjectionReference;
        this.drawScheduleConfiguration =
            values.contractType === JobContractType.FixedPrice
                ? values.drawScheduleValues
                : ({} as IDrawScheduleConfiguration);
    }

    jobName: string;
    groupedProjectType: number;
    jobStatus: number;
    projectManagers: number[];
    jobGroups: number[];
    jobsitePrefix: string;
    lotInfo: string;
    permitNumber: string;
    contractPrice: number;
    fundingType?: JobFundingType;
    defaultBillRetainagePercent?: number | null;
    defaultInvoiceRetainagePercent?: number | null;
    defaultProjectionReference?: number;
    workDays: number[];
    totalArea: number | null;
    notifyProjectManager: boolean;
    internalNotes: string | null;
    subNotes: string | null;
    projectedStartDate: moment.Moment | null;
    projectedCompletionDate: moment.Moment | null;
    actualStartDate: moment.Moment | null;
    syncActualStartEnd: boolean;
    actualCompletionDate: moment.Moment | null;
    enabled: boolean;
    individualPOLimit: number | undefined | null;
    totalPOLimit: number | undefined | null;
    enableGeofencing: boolean;
    includeTimeClockForBudget: boolean;
    includeTimeClockForCosts: boolean;
    includeAllowances: boolean;
    isAiaBillingJob: boolean;
    removeAllowancesWasConfirmed: boolean;
    updateRunningTotalForSelection: boolean;
    isTemplate: boolean;
    templateName: string;
    jobColor: number;
    address: AddressServiceObject;
    location: LocationEntity;
    customFields: ICustomFieldFormValues[];
    shouldHardUpdateCostItems?: boolean;
    defaultTaxGroupId: number;
    contractType?: number;
    markupMarginPercentType: number;
    defaultMarkup?: number;
    defaultMargin?: number;
    costTypesMarkup?: ICostTypesMarkup[];
    drawScheduleConfiguration?: IDrawScheduleConfiguration;
    jobBillAdditionalApproverGlobalUserIds: number[];
}

export interface ICostTypesMarkup {
    costType: number;
    markup?: number;
    margin?: number;
}

export class JobSaveRequest {
    constructor(values: IJobFormValues, entity: JobEntity) {
        this.jobInfo = new JobInfoSaveRequest(values.jobInfo, entity.jobInfo, values.customFields);
        this.jobInfo.jobBillAdditionalApproverGlobalUserIds =
            values.jobBillAdditionalApproverGlobalUserIds ?? [];
        this.ownerInfo = new OwnerInfoSaveRequest(
            entity.ownerInfo,
            values.ownerInfo,
            values.notificationPreferences,
        );
        this.viewingAccess = values.viewingAccess
            ? new JobViewingAccessSaveRequest(values.viewingAccess)
            : null;
        this.accountingJobsiteLinkingType = values.accountingEntityLinkingType;
        this.createNewAccountingJobOption = values.createNewAccountingEntityOption;
        this.includeOtherAccountingCostsInBudget = values.includeOtherAccountingCostsInBudget;
        this.includeQBCostsInProjectedCostsCalculation =
            values.includeQBCostsInProjectedCostsCalculation;
        this.accountingValues = new AccountingValues(
            values.accountingEntityLinkingType,
            values.createNewAccountingEntityOption,
            values.primaryAccountingOption,
            values.secondaryAccountingOption,
            values.accountingContactId,
            values.tertiaryAccountingOption,
        );
        this.billPayPaymentMethodId = values.billPayPaymentMethodId;
    }

    jobInfo: JobInfoSaveRequest;
    ownerInfo: OwnerInfoSaveRequest;
    viewingAccess: JobViewingAccessSaveRequest | null;
    accountingJobsiteLinkingType: JobsiteLinkingTypes;
    createNewAccountingJobOption: LinkingAction;
    includeOtherAccountingCostsInBudget: boolean;
    includeQBCostsInProjectedCostsCalculation: boolean;
    accountingValues: AccountingValues;
    billPayPaymentMethodId: number | null;

    /**
     * Opts the job being created into the new selections experience even when the builder's
     * default for new jobs is off. Only honored by the API on create, so it is left undefined
     * (and therefore omitted from the payload) on update rather than defaulted here — the wire
     * contract then matches the intent instead of relying on the server to ignore it.
     */
    createWithSelectionsReimagined?: boolean;
}

export class AccountingValues {
    constructor(
        public accountingEntityLinkingType: JobsiteLinkingTypes,
        public createNewAccountingJobOption: LinkingAction,
        public primaryAccountingOption?: string,
        public secondaryAccountingOption?: string,
        public accountingContactId?: string,
        public tertiaryAccountingOption?: string,
    ) {}
}

export class JobRunningTotal {
    constructor(data: any) {
        this.hasJobsitePermission = data.hasJobsitePermission;
        this.hasRunningTotalPermission = data.hasRunningTotalPermission;
        this.jobRunningTotal = data.jobRunningTotal;
        this.pricePerUnitArea = data.pricePerUnitArea;
        this.areaUnit = data.areaUnit;
        this.revisedClientPrice = data.revisedClientPrice;
        this.originalClientPrice = data.originalClientPrice;
        this.isWorksheetBudgeted = data.isWorksheetBudgeted;
        this.shouldUseNewClientPriceValues = data.shouldUseNewClientPriceValues;
        this.jobContractType = data.jobContractType;
    }

    hasJobsitePermission: boolean;
    hasRunningTotalPermission: boolean;
    jobRunningTotal: number | null;
    pricePerUnitArea: number | null;
    areaUnit: string | null;
    revisedClientPrice: number | null;
    originalClientPrice: number | null;
    isWorksheetBudgeted: boolean | null;
    shouldUseNewClientPriceValues: boolean | null;
    jobContractType: JobContractType | null;
}

export class OwnerInfoEntity {
    constructor(data: {
        ownerInfo: {
            showClientRemainingInvoiceBalance: { value: boolean };
            showClientBudgetDifference?: { value: boolean };
            [key: string]: any;
        };
    }) {
        this.contacts =
            data.ownerInfo.contacts?.map((contact: ContactEntity) => new ContactEntity(contact)) ??
            [];
        this.showJobPrice = data.ownerInfo.showJobPrice?.value;
        this.showClientRemainingInvoiceBalance =
            data.ownerInfo.showClientRemainingInvoiceBalance?.value;
        this.showClientBudgetDifference = data.ownerInfo.showClientBudgetDifference?.value ?? false;
        this.showBudget = data.ownerInfo.showBudget;
        this.showPOsAndBills = data.ownerInfo.showPOsAndBills;
        this.addWarrantyClaims = data.ownerInfo.addWarrantyClaims?.value;
        this.limitOwnerCalendar = data.ownerInfo.limitOwnerCalendar?.value.map(
            (item: any) => new BTSelectItem(item),
        );
        this.canSubmitCORequest = data.ownerInfo.canSubmitCORequest?.value;
        this.peekLockedSelections = data.ownerInfo.peekLockedSelections?.value;
        this.seePaymentsTab = data.ownerInfo.seePaymentsTab?.value;
        this.showCalendarPhases = data.ownerInfo.showPhases;
        this.showScheduleItems = data.ownerInfo.showScheduleItems;
        this.addedWarrantyClaimsEndDate = data.ownerInfo.addedWarrantyClaimsEndDate?.value
            ? moment(data.ownerInfo.addedWarrantyClaimsEndDate.value)
            : undefined;
        this.displayName = data.ownerInfo.contacts?.[0]?.displayName?.value;
        this.allowLoginAsOwner = data.ownerInfo.allowLoginAsOwner;
        this.linkedJobs =
            data.ownerInfo.linkedJobs?.map(
                (item: any) => new BTSelectItem<ILinkedJobDropdownExtraData>(item),
            ) ?? [];
        this.allowCreditCard = data.ownerInfo.allowCreditCards;
        this.allowChecks = data.ownerInfo.allowChecks;
        this.canAcceptChecks = data.ownerInfo.canAcceptChecks;
        this.areMerchantSettingsVisible = data.ownerInfo.areMerchantSettingsVisible;
        this.activationDate = moment(data.ownerInfo.activationDateRaw);
        this.notificationPreferences = data.ownerInfo.notificationPreferences;
        this.showNotificationPreferences = data.ownerInfo.showNotificationPreferences?.value;
        this.paymentOptionsSetup = new OnlinePaymentLimitsEntity(
            data.ownerInfo as OnlinePaymentLimitsApiData,
        );
        this.showProjectManagerPhoneNumbersToOwner =
            data.ownerInfo.showProjectManagerPhoneNumbersToOwner?.value;
        this.budgetDisplayPreference =
            data.ownerInfo.budgetDisplayPreference?.map(
                (item: BTSelectItem) => new BTSelectItem(item),
            ) ?? [];
        this.jobCostingBudgetDisplayPreference =
            data.ownerInfo.jobCostingBudgetDisplayPreference?.map(
                (item: BTSelectItem) => new BTSelectItem(item),
            ) ?? [];
        this.showJobCostingBudgetToOwner = data.ownerInfo.showJobCostingBudgetToOwner?.value;
        this.shouldHideOldBudget = data.ownerInfo.shouldHideOldBudget ?? false;
        this.showRelatedTagsToOwnerOnJobCostingBudget =
            data.ownerInfo?.showRelatedTagsToOwnerOnJobCostingBudget?.value ?? false;
        this.hasContactSavedToJob = data.ownerInfo?.contacts?.length > 0;
    }
    displayName: string;
    contacts: ContactEntity[];
    showJobPrice: boolean;
    showClientRemainingInvoiceBalance: boolean;
    showClientBudgetDifference: boolean;
    showBudget: boolean;
    showPOsAndBills: boolean;
    addWarrantyClaims: boolean;
    limitOwnerCalendar: BTSelectItem[];
    canSubmitCORequest: boolean;
    peekLockedSelections: boolean;
    seePaymentsTab: boolean;
    showCalendarPhases: boolean;
    showScheduleItems: boolean;
    addedWarrantyClaimsEndDate: moment.Moment | undefined;
    allowLoginAsOwner: boolean;
    linkedJobs: BTSelectItem<ILinkedJobDropdownExtraData>[];
    allowCreditCard: boolean;
    allowChecks: boolean;
    canAcceptChecks: boolean;
    areMerchantSettingsVisible: boolean;
    activationDate: Moment;
    notificationPreferences: INotificationPreferenceData;
    showNotificationPreferences: boolean;
    paymentOptionsSetup: OnlinePaymentLimitsEntity;
    showProjectManagerPhoneNumbersToOwner: boolean;
    budgetDisplayPreference: BTSelectItem<IBudgetDisplayPreferenceExtraData>[];
    jobCostingBudgetDisplayPreference: BTSelectItem<IBudgetDisplayPreferenceExtraData>[];
    showJobCostingBudgetToOwner: boolean;
    shouldHideOldBudget: boolean;
    showRelatedTagsToOwnerOnJobCostingBudget: boolean;
    hasContactSavedToJob: boolean;
}

export class JobPermissionWizardEntity {
    constructor(data: any) {
        this.autoAddedSubs = data.autoAddedSubs;
        this.subs = data.subs;
        this.users = data.users;
        this.conflicts = data.conflicts;
        this.calendarOnline = data.calendarOnline;
        this.canSetBaseline = data.canSetBaseline;
        this.skipAddUsers = data.skipAddUsers || false;
        this.baselineSet = data.baselineSet;
    }
    autoAddedSubs: number[];
    subs: number[];
    users: number[];
    conflicts: UserWithConflicts[];
    calendarOnline: boolean;
    canSetBaseline: boolean;
    skipAddUsers: boolean;
    baselineSet: boolean;
}

export interface ILinkedJobDropdownExtraData {
    jobName: string;
    ownerName: string;
    disabled: boolean;
    linkedIds: number[];
    status: ActivationStatus;
}

export interface IBudgetDisplayPreferenceExtraData {
    disabled: boolean;
}

export class ValueWithMinMax {
    constructor(data: any) {
        if (!data) {
            return;
        }

        this.max = data.max !== null ? data.max : undefined;
        this.min = data.min !== null ? data.min : undefined;
    }

    max: number;
    min: number;
}

export class POLimit extends ValueWithMinMax {
    constructor(data: any) {
        super(data);
        if (!data) {
            return;
        }

        this.value = data.value;
        this.placeholder = data.placeholder !== null ? data.placeholder : undefined;
    }

    value: number | null;
    placeholder: string;
}

export class JobDeleteResponse {}

export class JobSaveResponse {
    constructor(data: any) {
        this.id = data.id;
        this.failedFields = data.failedFields;
        this.addedInactiveSubs = data.addedInactiveSubs
            ? data.addedInactiveSubs.map((sub: any) => new BTSelectItem<SubsToInviteExtraData>(sub))
            : [];
        this.subInviteMessage = data.subInviteMessage ? data.subInviteMessage.value : undefined;
        this.accountingCreationFailed = data.accountingCreationFailed;
        this.accountingType = data.accountingType;
    }
    id: number;
    failedFields?: ServiceValidation[];
    addedInactiveSubs?: BTSelectItem<SubsToInviteExtraData>[];
    subInviteMessage?: string;
    accountingCreationFailed: boolean;
    accountingType: string;
}

export interface IAddToJobRequest {
    jobId: number;
    subs: number[];
    logins: number[];
}

export class AddToJobResponse {
    subs: number[];
}

export interface IJobPermissionRequest {
    subIds?: number[];
    internalUserIds?: number[];
    jobIds: number[];
}

export class JobPermissionResponse {
    constructor(data: any) {
        this.headerMessage = data.headerMessage;
        this.headerDescription = data.headerDescription;
        this.permissions = data.permissions;
        this.skip = data.skip;
        this.titleText = data.titleText;
        this.subTitleText = data.subTitleText;
    }
    headerMessage: string;
    headerDescription: string;
    permissions: JobPermission[];
    skip: BTEntityAction;
    titleText: string;
    subTitleText: string;
}

export class JobPermissionWizardResponse {
    constructor(data: any) {
        this.autoAddedSubs = data.autoAddedSubs;
        this.subs = data.subs;
        this.users = data.users;
        this.conflicts = data.conflicts;
        this.calendarOnline = data.calendarOnline;
        this.canSetBaseline = data.canSetBaseline;
        this.skipAddUsers = data.skipAddUsers || false;
        this.baselineSet = data.baselineSet;
    }
    autoAddedSubs: number[];
    subs: number[];
    users: number[];
    conflicts: UserWithConflicts[];
    calendarOnline: boolean;
    canSetBaseline: boolean;
    skipAddUsers: boolean;
    baselineSet: boolean;
}

export class UpdateUserPermissionResponse {}
export class UpdateSubPermissionResponse {}

export class UserListResponse {
    constructor(data?: any) {
        this.users = data.groupedUserList
            ? data.groupedUserList.value[0].options.map((item: any) => new BTSelectItem(item))
            : undefined;
    }
    users?: BTSelectItem[];
}

export class LinkableJobListResponse {
    constructor(data: any) {
        this.linkableJobs =
            data.map((item: any) => new BTSelectItem<ILinkedJobDropdownExtraData>(item)) ?? [];
    }
    linkableJobs: BTSelectItem<ILinkedJobDropdownExtraData>[];
}

export class JobPermission {
    value: boolean;
    header: boolean;
    id: number;
    name: string;
    formattedName: string;
    count: number;
    selected: boolean;
    showFields: boolean;
    type: string;
    enabled: boolean;
    extraData: object;
    nestedOptions: JobPermission[];
}

export class SubsToInviteExtraData {
    inviteStatus: string;
    email: string;
}
export class JobColorExtraData {
    hex: string;
}

export enum MappingStatus {
    Mapped = 1,
    NotMapped = 2,
    MappedManually = 3,
}

export const getPinTypeFromMappingStatus = (mappingStatus: MappingStatus | null) => {
    if (mappingStatus === MappingStatus.Mapped) {
        return PinTypes.Default;
    } else if (mappingStatus === MappingStatus.MappedManually) {
        return PinTypes.Edited;
    } else {
        return PinTypes.Inactive;
    }
};

export class ReassignConfirmationResponse {
    constructor(data: any) {
        this.dataRows = data.data.map((row: any) => {
            return {
                sourceType: row.sourceType,
                title: row.title,
                startDate: row["Start Date"],
                endDate: row["End Date"],
                deadLine: row.Deadline,
            };
        });

        this.pageNumber = data.page;
        this.pageSize = data.pageSize;
        this.total = data.records;
    }

    dataRows: ReassignItemRow[];
    pageNumber: number;
    pageSize: number;
    total: number;
}
export class MassReassignResponse {}

export class TransferItemDetailsResponse {
    constructor(data: any) {
        this.hasJobsiteAccess = data.hasJobsiteAccess;
        this.needsJobsiteAccess = data.needsJobsiteAccess;
        this.showNotifyCheckbox = data.showNotifyCheckbox;
    }
    hasJobsiteAccess: boolean;
    needsJobsiteAccess: boolean;
    showNotifyCheckbox: boolean;
}

export class TransferItemDetailsEntity {
    constructor(data: TransferItemDetailsResponse) {
        let grantViewingAccess = false;
        let grantViewingAccessVisible = true;
        let forceJobsiteAccessVisible = false;

        if (data.hasJobsiteAccess) {
            grantViewingAccessVisible = false;
        } else {
            if (data.needsJobsiteAccess) {
                grantViewingAccessVisible = false;
                forceJobsiteAccessVisible = true;
                grantViewingAccess = true;
            } else {
                grantViewingAccessVisible = true;
                grantViewingAccess = true;
            }
        }
        this.notify = data.showNotifyCheckbox;
        this.notifyVisible = data.showNotifyCheckbox;
        this.forceJobsiteAccessVisible = forceJobsiteAccessVisible;
        this.grantViewingAccessVisible = grantViewingAccessVisible;
        this.grantViewingAccess = grantViewingAccess;
    }

    notify: boolean;
    notifyVisible: boolean;
    forceJobsiteAccessVisible: boolean;
    grantViewingAccessVisible: boolean;
    grantViewingAccess: boolean;
}

export class ReassignItemRow {
    sourceType: string;
    title: string;
    startDate: string;
    endDate: string;
    deadLine: string;
}

export interface IReassignConfirmationRequest {
    firstRow: number;
    lastRow: number;
    previousId: number;
    pageNumber: number;
    pageSize: number;
}

export interface IBudgetPrefOptionTabProps {
    onBudgetPrefOptionChange: (
        e: CheckboxChangeEvent,
        budgetColumns: BudgetColumn[],
        jobCostingBudgetColumns: JobCostingBudgetColumns[],
    ) => void;
}

// Job level single draw configuration
export interface IDrawConfiguration extends IDraw {}

// Job level draw schedule configuration
export interface IDrawScheduleConfiguration {
    draws: IDrawConfiguration[];
}
